import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { FileUploader } from 'ng2-file-upload';

import { FileUploaderService } from '../file-uploader.service';
import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import environment from '../../../environments/environment';

export interface GableAttachmentUploadResult {
    uploadedFileId: number;
    fileName: string;
}

export interface GableAttachmentUploadError {
    message: string;
    status?: number;
}

/** Attachments only support PDF today — matches the drawer's attach-a-PDF affordance. */
export const ALLOWED_ATTACHMENT_EXTENSIONS = ['pdf'];

/**
 * Uploads a single PDF attachment for the Gable drawer composer. Modeled on
 * ImportFileUploaderService — same /file/upload endpoint, same
 * uploadedFileId contract, just PDF-only validation.
 */
@Injectable({
    providedIn: 'root',
})
export class GableAttachmentUploaderService {
    private _fileUploader: FileUploader;
    private _uploadSubject: Subject<GableAttachmentUploadResult>;
    private _currentFileName: string;

    constructor(private _fileUploaderService: FileUploaderService) {}

    uploadFile(file: File): Observable<GableAttachmentUploadResult> {
        const jwt = LegFiJwtService.read();
        if (!jwt) {
            return new Observable(subscriber => {
                subscriber.error({ message: 'Session expired. Please refresh and try again.' } as GableAttachmentUploadError);
            });
        }

        const validationError = this.validateFile(file);
        if (validationError) {
            return new Observable(subscriber => {
                subscriber.error({ message: validationError } as GableAttachmentUploadError);
            });
        }

        this._uploadSubject = new Subject<GableAttachmentUploadResult>();
        this._currentFileName = file.name;

        this._initUploader();

        this._fileUploader.clearQueue();
        this._fileUploader.addToQueue([file]);
        this._fileUploader.uploadAll();

        return this._uploadSubject.asObservable();
    }

    validateFile(file: File): string | null {
        const fileName = file.name.toLowerCase();
        const extension = fileName.split('.').pop();

        if (!extension || !ALLOWED_ATTACHMENT_EXTENSIONS.includes(extension)) {
            return 'Only PDF files can be attached.';
        }

        if (file.size > environment.WorkerServerFileSizeLimit) {
            const limitMb = Math.ceil((environment.WorkerServerFileSizeLimit / 1024) / 1024);
            return `File exceeds the ${limitMb}MB limit.`;
        }

        return null;
    }

    getAcceptedMimeTypes(): string {
        return '.pdf,application/pdf';
    }

    private _initUploader(): void {
        if (this._fileUploader) {
            return;
        }

        const uploadUrl = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.UploadFile());

        this._fileUploader = this._fileUploaderService.initUploader({
            url: uploadUrl,
        });

        this._fileUploader.options.autoUpload = false;

        this._fileUploader.onBuildItemForm = (fileItem: any, form: any) => {
            form.append('fileName', fileItem.file.name);
        };

        this._fileUploader.onAfterAddingFile = (file): any => {
            if (this._fileUploader.queue.length > 1) {
                this._fileUploader.queue.splice(0, this._fileUploader.queue.length - 1);
            }
        };

        this._fileUploader.onCompleteItem = (item, response, status) => {
            if (status >= 200 && status < 300) {
                try {
                    const res = JSON.parse(response);
                    if (res.id) {
                        this._uploadSubject.next({
                            uploadedFileId: res.id,
                            fileName: this._currentFileName,
                        });
                        this._uploadSubject.complete();
                    } else {
                        this._uploadSubject.error({
                            message: 'Upload succeeded but no file ID returned.',
                            status,
                        } as GableAttachmentUploadError);
                    }
                } catch (e) {
                    this._uploadSubject.error({
                        message: 'Failed to parse upload response.',
                        status,
                    } as GableAttachmentUploadError);
                }
            }
        };

        this._fileUploader.onErrorItem = (fileItem: any, response: string, status: number) => {
            let errorMessage = 'Failed to upload file. Please try again.';

            if (response) {
                try {
                    const errorResponse = JSON.parse(response);
                    errorMessage = errorResponse['error'] || errorMessage;
                } catch (e) {
                    // use default
                }
            }

            this._uploadSubject.error({
                message: errorMessage,
                status,
            } as GableAttachmentUploadError);
        };
    }
}
